import React from 'react';
import uniqid from 'uniqid';

const NODE_TYPES = {
  NATIVE: 'native',
  COMPONENT: 'component',
};

const getNode = (node, nestedInSsr) => {
  const nodeName = typeof node?.type === 'string' ? node?.type : node?.type?.name;
  const rootDisplayName = node?.type?.rootDisplayName;

  if (!nodeName || !node?.props) {
    return { nodeType: null, node: null, nodeName: null, nodeMeta: {} };
  }

  const isNativeNode = nodeName[0] !== nodeName[0].toUpperCase();
  const nodeType = isNativeNode ? NODE_TYPES.NATIVE : NODE_TYPES.COMPONENT;

  const isServerSideDefault = rootDisplayName === 'ServerSideDefault';
  const isFlaggedForSsr = node.props.pureServerside === true;
  const isRootSsrNode = !nestedInSsr && isServerSideDefault;
  const isFederatedApp = nodeType === NODE_TYPES.COMPONENT && node.type.app;

  const nodeMeta = {
    isFederatedApp,
    isRootSsrNode,
    isServerSideDefault,
    isFlaggedForSsr,
  };

  return { nodeType, node, nodeName, nodeMeta };
};

const isBaseValue = (node) => {
  if (
    typeof node === 'string' ||
    typeof node === 'number' ||
    typeof node === 'boolean' ||
    typeof node === 'symbol' ||
    typeof node === 'bigint' ||
    typeof node === 'function' ||
    node === null ||
    node === undefined
  ) {
    return true;
  }

  return false;
};

export const walk = (rootNode, reviveNode) => {
  if (!rootNode) {
    return undefined;
  }

  const traverse = (item, parent, nestedInSsr = false) => {
    if (isBaseValue(item)) {
      return item;
    }

    if (Array.isArray(item)) {
      return item.map((child) => traverse(child, parent, nestedInSsr));
    }

    const nodeInfo = getNode(item, nestedInSsr);
    let { node } = nodeInfo;
    const { nodeType, nodeMeta } = nodeInfo;

    if (!node) {
      const traversedObjEntries = Object.entries(item).map(([key, value]) => [
        key,
        traverse(value, parent, nestedInSsr),
      ]);

      return Object.fromEntries(traversedObjEntries);
    }

    const {
      isFederatedApp,
      isRootSsrNode,
      isFlaggedForSsr,
      isServerSideDefault,
    } = nodeMeta;

    const hKey = uniqid('h-')
    const newProps = traverse(item.props, {...item, hKey}, isRootSsrNode || nestedInSsr);

    if (React.isValidElement(node)) {
      node = React.cloneElement(node, newProps);
    } else {
      node.props = newProps;
    }

    const revivedNode = reviveNode(nodeType, node, {
      hKey,
      isFederatedApp,
      isRootSsrNode,
      isFlaggedForSsr,
      isServerSideDefault,
      nestedInSsr,
      parent,
    });

    if (!revivedNode) {
      return node;
    }

    return revivedNode;
  };

  return traverse(rootNode);
};
